import { useEffect, useState } from 'react';
import Modal from '~/components/Modal';
import SelectClient from '~/components/clients/SelectClient';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline/index';
import InfoMessageBlock from '~/components/InfoMessageBlock';
import useBusinessPostApi from '~/lib/useBusinessPostApi';
import { useClientsQuery } from '~/lib/Queries';
import * as Toast from '~/lib/Toast';
import ErrorMessage from '~/components/ErrorMessage';

const SelectMergeClient = ({ fromClient, toClient, setToClient, onNext }) => {
    const [error, setError] = useState(false);
    return (
        <div>
            <h2>Merge {fromClient.name}</h2>

            <div className="panel-label mt-4">With Client</div>
            <SelectClient onChange={setToClient} value={toClient} />
            {!!error && (
                <ErrorMessage className="mt-2">
                    Please select a client
                </ErrorMessage>
            )}
            <button
                type="button"
                className="btn-pill mt-4"
                onClick={() => {
                    if (toClient?.id) {
                        setError(false);
                        onNext();
                    } else {
                        setError(true);
                    }
                }}
            >
                Next
            </button>
        </div>
    );
};

const ClientPanel = ({ client, className }) => {
    return (
        <div className={`panel p-4 text-center ${className}`}>
            <div>{client.name}</div>
            <div className="text-gray-500 text-xs">{client.email}</div>
        </div>
    );
};

const MergeClients = ({
    fromClient,
    toClient,
    onBack,
    onClose,
    onMergeSuccess,
}) => {
    const [leftClient, setLeftClient] = useState(fromClient);
    const [rightClient, setRightClient] = useState(toClient);

    const { refetch } = useClientsQuery();

    const { save, isSaving } = useBusinessPostApi(`/clients/merge`);

    const combinedClient = {
        name: rightClient.name || leftClient.name,
        email: rightClient.email,
    };

    function doSave() {
        save({
            fromClient: leftClient.id,
            toClient: rightClient.id,
        }).then(result => {
            refetch();
            onMergeSuccess();
            onClose();
            Toast.success(
                `Successfully merged ${fromClient.name} into ${toClient.name}`,
            );
        });
    }

    return (
        <div>
            <h2>Merge Clients</h2>
            <div className="flex mt-4 items-center space-x-3">
                <ClientPanel client={leftClient} className="flex-1" />
                <button
                    className="btn-light-circle"
                    onClick={() => {
                        setLeftClient(rightClient);
                        setRightClient(leftClient);
                    }}
                >
                    <ArrowsRightLeftIcon className={'w-4 h-4'} />
                </button>
                <ClientPanel client={rightClient} className="flex-1" />
                <div>=</div>
                <ClientPanel
                    client={combinedClient}
                    className="border-2 border-primary-700 flex-1"
                />
            </div>
            <InfoMessageBlock className="my-4" showIcon={true}>
                Forms, events, and all other data from {leftClient.name} will be
                merged into {rightClient.name}.
                <div className="mt-2">
                    This is permanent and cannot be undone.
                </div>
            </InfoMessageBlock>
            <div className="flex items-center mt-4">
                <button type="button" className="btn-save" onClick={doSave}>
                    {isSaving ? 'Merging...' : 'Merge Clients'}
                </button>
                <button
                    type="button"
                    className="ml-4 btn-gray-pill-lg"
                    onClick={onBack}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

const MergeClientsModal = ({ isOpen, onClose, fromClient, onMergeSuccess }) => {
    const [step, setStep] = useState('');
    const [toClient, setToClient] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            setStep('');
        }
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            widthClass={!step ? '' : `md:min-w-[800px]`}
        >
            {!step && (
                <SelectMergeClient
                    fromClient={fromClient}
                    toClient={toClient}
                    setToClient={setToClient}
                    onNext={() => setStep('merge')}
                />
            )}
            {step === 'merge' && (
                <MergeClients
                    fromClient={fromClient}
                    toClient={toClient}
                    onClose={onClose}
                    onBack={() => setStep('')}
                    onMergeSuccess={onMergeSuccess}
                />
            )}
        </Modal>
    );
};

export default MergeClientsModal;
